import { copy } from '@ohear/shared'
import { Button, useToast } from '@ohear/ui'
/**
 * Bulk CSV rate import.
 *
 * Producer-side form: upload a CSV with two required columns,
 * `character_code` and `line_rate_cents`, preview the first 10 rows + total
 * row count, see a per-row error report, then apply.
 *
 * CSV parsing is hand-rolled (no papaparse to keep deps minimal). Supports
 *   - comma separator
 *   - double-quoted fields (escape `""` inside quotes)
 *   - CRLF or LF line endings
 *   - trailing empty lines (ignored)
 *
 * Other columns are ignored. Rates must be positive integers (cents). Any
 * malformed row is reported in `errors` and excluded from the apply payload;
 * the operator can also download an `errors.csv` to fix offline.
 */
import { type ChangeEvent, useMemo, useState } from 'react'
import { type BulkRateRow, useBulkUpdateCharacterRates } from '../../lib/api/characters'
import { useT } from '../../lib/i18n/use-t'

export interface RateImportFormProps {
  projectId: string
  onDone?: () => void
}

interface ParsedRow extends BulkRateRow {
  /** 1-based source row number (matches the file, header = 0). */
  rowNumber: number
}

interface ErrorRow {
  rowNumber: number
  raw: string
  reason: string
}

interface ParseResult {
  rows: ParsedRow[]
  errors: ErrorRow[]
}

export function RateImportForm({ projectId, onDone }: RateImportFormProps) {
  const t = useT()
  const toast = useToast()
  const mutation = useBulkUpdateCharacterRates(projectId)

  const [fileName, setFileName] = useState<string | null>(null)
  const [rawCsv, setRawCsv] = useState<string>('')

  const parsed: ParseResult | null = useMemo(() => {
    if (!rawCsv) return null
    return parseRateCsv(rawCsv)
  }, [rawCsv])

  const onPickFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0]
    if (!file) return
    setFileName(file.name)
    const text = await file.text()
    setRawCsv(text)
  }

  const onApply = async () => {
    if (!parsed || parsed.rows.length === 0) return
    const payload: BulkRateRow[] = parsed.rows.map((r) => ({
      character_code: r.character_code,
      line_rate_cents: r.line_rate_cents,
    }))
    try {
      const result = await mutation.mutateAsync(payload)
      toast.show({
        severity: 'success',
        title: `Applied ${result.applied_count} rate${result.applied_count === 1 ? '' : 's'}`,
        description:
          result.skipped_codes.length > 0
            ? `${result.skipped_codes.length} unknown character code${
                result.skipped_codes.length === 1 ? '' : 's'
              } skipped: ${result.skipped_codes.join(', ')}`
            : undefined,
      })
      onDone?.()
    } catch (err) {
      toast.show({
        severity: 'error',
        title: 'Failed to import rates',
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  const downloadErrors = () => {
    if (!parsed || parsed.errors.length === 0) return
    const lines = [
      'row_number,raw,reason',
      ...parsed.errors.map((e) => `${e.rowNumber},${csvEscape(e.raw)},${csvEscape(e.reason)}`),
    ]
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'rate-import-errors.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h3 className="m-0 text-[15px] font-semibold text-[var(--text)]">
          {t(copy.admin.HEADINGS.import_rates_csv)}
        </h3>
        <p className="m-0 text-[13px] text-[var(--text-2)]">
          Required columns: <code>character_code</code>, <code>line_rate_cents</code>. Other columns
          are ignored. Rates must be positive integer cents.
        </p>
      </header>

      <div>
        <label
          htmlFor="rate-csv-input"
          className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-3)] px-3 text-[13px] text-[var(--text)] hover:bg-[var(--bg-2)]"
        >
          {fileName ? `Replace file (${fileName})` : 'Choose .csv file'}
        </label>
        <input
          id="rate-csv-input"
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          onChange={onPickFile}
        />
      </div>

      {parsed && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-[13px] text-[var(--text-2)]">
            <span>
              <span className="font-semibold tabular-nums text-[var(--text)]">
                {parsed.rows.length}
              </span>{' '}
              valid row{parsed.rows.length === 1 ? '' : 's'}
            </span>
            {parsed.errors.length > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  <span className="font-semibold tabular-nums text-[var(--amber,#f59e0b)]">
                    {parsed.errors.length}
                  </span>{' '}
                  error{parsed.errors.length === 1 ? '' : 's'}
                </span>
                <Button size="sm" variant="ghost" onClick={downloadErrors}>
                  Download errors.csv
                </Button>
              </>
            )}
          </div>

          {parsed.rows.length > 0 && (
            <div className="overflow-x-auto rounded-[var(--radius)] border border-[var(--border)]">
              <table className="w-full text-[12px]">
                <thead className="bg-[var(--bg-3)] text-[11px] uppercase tracking-wide text-[var(--text-2)]">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Row</th>
                    <th className="px-3 py-2 text-left font-medium">Character code</th>
                    <th className="px-3 py-2 text-right font-medium">Line rate (cents)</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.slice(0, 10).map((row) => (
                    <tr key={row.rowNumber} className="border-t border-[var(--border)]">
                      <td className="px-3 py-1.5 tabular-nums text-[var(--text-3)]">
                        {row.rowNumber}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[var(--text)]">
                        {row.character_code}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-[var(--text)]">
                        {row.line_rate_cents.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsed.rows.length > 10 && (
                <div className="border-t border-[var(--border)] bg-[var(--bg-3)] px-3 py-1.5 text-[11px] text-[var(--text-3)]">
                  Showing first 10 of {parsed.rows.length} rows.
                </div>
              )}
            </div>
          )}

          {parsed.errors.length > 0 && (
            <div className="overflow-x-auto rounded-[var(--radius)] border border-[var(--amber,#f59e0b)]/40 bg-[var(--amber,#f59e0b)]/5">
              <table className="w-full text-[12px]">
                <thead className="text-[11px] uppercase tracking-wide text-[var(--text-2)]">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Row</th>
                    <th className="px-3 py-2 text-left font-medium">Reason</th>
                    <th className="px-3 py-2 text-left font-medium">Raw</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.errors.slice(0, 20).map((err) => (
                    <tr key={err.rowNumber} className="border-t border-[var(--border)]">
                      <td className="px-3 py-1.5 tabular-nums text-[var(--text-3)]">
                        {err.rowNumber}
                      </td>
                      <td className="px-3 py-1.5 text-[var(--text)]">{err.reason}</td>
                      <td className="px-3 py-1.5 font-mono text-[var(--text-3)]">{err.raw}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsed.errors.length > 20 && (
                <div className="border-t border-[var(--border)] px-3 py-1.5 text-[11px] text-[var(--text-3)]">
                  Showing first 20 of {parsed.errors.length} errors. Download for the full list.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <footer className="flex items-center justify-end gap-2 border-t border-[var(--border)] pt-4">
        <Button variant="ghost" onClick={onDone} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onApply}
          loading={mutation.isPending}
          disabled={!parsed || parsed.rows.length === 0}
        >
          Apply {parsed ? `(${parsed.rows.length})` : ''}
        </Button>
      </footer>
    </div>
  )
}

// ── CSV parsing helpers ───────────────────────────────────────────────────────

/**
 * Minimal CSV parser. Handles the cases real producer CSVs throw at us:
 *   - quoted fields with commas inside
 *   - escaped quotes (`""`)
 *   - CRLF + LF line endings
 *   - blank trailing lines
 *
 * Anything fancier (multi-char delimiters, BOMs other than UTF-8) is out of
 * scope; we'd reach for papaparse if those become needs.
 */
export function parseRateCsv(input: string): ParseResult {
  // Strip a leading BOM if present.
  const text = input.replace(/^﻿/, '')
  const allRows = splitCsvRows(text)
  if (allRows.length === 0) return { rows: [], errors: [] }

  const headerRow = allRows[0] ?? []
  const codeIdx = headerRow.findIndex((c) => c.trim().toLowerCase() === 'character_code')
  const rateIdx = headerRow.findIndex((c) => c.trim().toLowerCase() === 'line_rate_cents')

  if (codeIdx < 0 || rateIdx < 0) {
    return {
      rows: [],
      errors: [
        {
          rowNumber: 1,
          raw: headerRow.join(','),
          reason: 'Header missing required columns. Need "character_code" and "line_rate_cents".',
        },
      ],
    }
  }

  const rows: ParsedRow[] = []
  const errors: ErrorRow[] = []

  for (let i = 1; i < allRows.length; i += 1) {
    const cells = allRows[i]
    if (!cells) continue
    if (cells.length === 0 || (cells.length === 1 && cells[0]?.trim() === '')) continue
    const rowNumber = i + 1
    const code = (cells[codeIdx] ?? '').trim()
    const rateRaw = (cells[rateIdx] ?? '').trim()
    if (!code) {
      errors.push({ rowNumber, raw: cells.join(','), reason: 'Missing character_code' })
      continue
    }
    if (!rateRaw) {
      errors.push({ rowNumber, raw: cells.join(','), reason: 'Missing line_rate_cents' })
      continue
    }
    if (!/^\d+$/.test(rateRaw)) {
      errors.push({
        rowNumber,
        raw: cells.join(','),
        reason: 'line_rate_cents must be a positive integer (cents, no decimals)',
      })
      continue
    }
    const cents = Number.parseInt(rateRaw, 10)
    if (!Number.isFinite(cents) || cents <= 0) {
      errors.push({
        rowNumber,
        raw: cells.join(','),
        reason: 'line_rate_cents must be greater than 0',
      })
      continue
    }
    rows.push({ rowNumber, character_code: code, line_rate_cents: cents })
  }

  return { rows, errors }
}

/** Split a CSV string into an array of cell arrays. */
function splitCsvRows(text: string): string[][] {
  const out: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        cell += ch
      }
      continue
    }
    if (ch === '"') {
      inQuotes = true
      continue
    }
    if (ch === ',') {
      row.push(cell)
      cell = ''
      continue
    }
    if (ch === '\r') continue
    if (ch === '\n') {
      row.push(cell)
      cell = ''
      out.push(row)
      row = []
      continue
    }
    cell += ch
  }
  // Last cell / row.
  if (cell.length > 0 || row.length > 0) {
    row.push(cell)
    out.push(row)
  }
  return out
}

/** Conservative CSV escape for the errors download. */
function csvEscape(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}
